import { z } from 'zod';
import type { TravellerImportRowDTO } from '@/api/trafikportalen';
import { findCreatedTraveller } from './createdTraveller';
import { ParsedRow } from './fileParser';
import { EMAIL_REGEX } from './validation';

export const TRAVELLER_FIELD_MAPPING: Record<string, string[]> = {
  email: ['email', 'e-post', 'epost', 'e-postadress'],
  first_name: ['first_name', 'förnamn', 'fornamn', 'firstname', 'first name'],
  last_name: ['last_name', 'efternamn', 'lastname', 'last name'],
  national_identity_number: [
    'national_identity_number',
    'personnummer',
    'personal number',
    'nin',
  ],
  bobcat_private_email: [
    'bobcat_private_email',
    'bobcat_traveller_email',
    'app email',
    'app-epost',
    'appepost',
    'email i appen',
    'epost i appen',
    'e-post i appen',
    'e-post i appen / mina sidor',
  ],
  phone: [
    'phone',
    'telefon',
    'telephone',
    'mobilnummer',
    'mobil',
    'telefonnummer',
    'tel',
  ],
  department: ['department', 'avdelning', 'dept'],
};

export interface TravellerImportRowData extends TravellerImportRowDTO {
  bobcat_private_email?: string;
}

export interface TravellerImportPreview {
  row: number;
  data: TravellerImportRowData;
  status: 'valid' | 'invalid' | 'warning';
  errors: Record<string, string>;
  warnings: Record<string, string>;
}

export interface TravellerImportBobcatLink {
  email: string;
  firstName?: string;
  lastName?: string;
  bobcatPrivateEmail: string;
}

export interface TravellerImportBobcatMatch {
  traveller: {
    id: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    created_at?: string;
    bobcat_private_email?: string;
  };
}

export interface ResolvedTravellerImportBobcatLink {
  travellerId: string;
  name: {
    first_name?: string;
    last_name?: string;
  };
  bobcatPrivateEmail: string;
  alreadyLinked: boolean;
}

const travellerImportSchema = z
  .object({
    email: z.string().email().regex(EMAIL_REGEX, {
      message: 'Invalid email format',
    }),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    national_identity_number: z.string().optional(),
    bobcat_private_email: z
      .string()
      .email()
      .regex(EMAIL_REGEX, {
        message: 'Invalid email format',
      })
      .optional()
      .or(z.literal('')),
    phone: z.string().optional(),
    department: z.string().optional(),
  })
  .refine((data) => data.email !== undefined && data.email !== '', {
    message: 'Email is required',
    path: ['email'],
  });

export const toTravellerImportRowDTO = (
  data: TravellerImportRowData,
): TravellerImportRowDTO => {
  const row = { ...data };
  delete row.bobcat_private_email;
  return row;
};

export const getTravellerImportBobcatLinks = (
  results: TravellerImportPreview[],
  failedEmails: Iterable<string>,
): TravellerImportBobcatLink[] => {
  const failed = new Set(
    [...failedEmails].map((email) => email.trim().toLowerCase()),
  );
  const linksByEmail = new Map<string, TravellerImportBobcatLink>();

  results.forEach((result) => {
    if (result.status === 'invalid') {
      return;
    }

    const email = result.data.email.trim().toLowerCase();
    const bobcatPrivateEmail = result.data.bobcat_private_email
      ?.trim()
      .toLowerCase();

    if (!bobcatPrivateEmail || failed.has(email)) {
      return;
    }

    linksByEmail.set(email, {
      email,
      firstName: result.data.first_name,
      lastName: result.data.last_name,
      bobcatPrivateEmail,
    });
  });

  return [...linksByEmail.values()];
};

export const resolveTravellerImportBobcatLink = (
  travellers: TravellerImportBobcatMatch[],
  link: TravellerImportBobcatLink,
): ResolvedTravellerImportBobcatLink | undefined => {
  const match = findCreatedTraveller(travellers, {
    email: link.email,
    firstName: link.firstName,
    lastName: link.lastName,
  });

  if (!match) {
    return undefined;
  }

  const currentLinkedEmail =
    match.traveller.bobcat_private_email?.toLowerCase() ?? '';

  return {
    travellerId: match.traveller.id,
    name: {
      first_name: match.traveller.first_name,
      last_name: match.traveller.last_name,
    },
    bobcatPrivateEmail: link.bobcatPrivateEmail,
    alreadyLinked: currentLinkedEmail === link.bobcatPrivateEmail,
  };
};

export const normalizeTravellerRow = (
  row: ParsedRow,
  headerMap: Record<string, string>,
): TravellerImportRowData => {
  const normalized: TravellerImportRowData = { email: '' };

  Object.entries(row).forEach(([key, value]) => {
    const mappedKey = headerMap[key] as keyof TravellerImportRowData;
    if (mappedKey && value !== undefined && value !== '') {
      if (typeof value === 'string') {
        normalized[mappedKey] = value.trim();
      } else {
        normalized[mappedKey] = value.toString();
      }
    }
  });

  return normalized;
};

export const validateTravellerRow = (
  data: TravellerImportRowData,
  rowNumber: number,
): TravellerImportPreview => {
  const errors: Record<string, string> = {};
  const warnings: Record<string, string> = {};

  try {
    const processedData = { ...data };
    if (processedData.phone === '') {
      processedData.phone = undefined;
    }
    if (processedData.national_identity_number === '') {
      processedData.national_identity_number = undefined;
    }
    if (processedData.bobcat_private_email === '') {
      processedData.bobcat_private_email = undefined;
    }

    travellerImportSchema.parse(processedData);

    if (!data.first_name) {
      warnings.first_name = 'Förnamn saknas';
    }
    if (!data.last_name) {
      warnings.last_name = 'Efternamn saknas';
    }

    const status =
      Object.keys(warnings).length > 0
        ? ('warning' as const)
        : ('valid' as const);

    return {
      row: rowNumber,
      data: processedData,
      status,
      errors,
      warnings,
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      error.errors.forEach((err) => {
        const field = err.path.join('.');
        errors[field] = err.message;
      });
    } else {
      errors.general = 'Okänt valideringsfel';
    }

    return {
      row: rowNumber,
      data,
      status: 'invalid',
      errors,
      warnings,
    };
  }
};

export const processTravellerImport = (
  rows: ParsedRow[],
  headerMap: Record<string, string>,
): TravellerImportPreview[] => {
  return rows.map((row, index) => {
    const normalized = normalizeTravellerRow(row, headerMap);
    const result = validateTravellerRow(normalized, index + 2); // +2 because row 1 is headers

    return result;
  });
};
